import { useAuthStore } from '@/stores/auth.store'

/**
 * Best-effort localStorage persistence for in-progress forms (invoice/entry
 * drafts), scoped per org so switching tenants never leaks a draft across
 * accounts. Storage failures (private mode, quota) are swallowed — autosave
 * is a convenience, not a guarantee.
 */
export function useDraftStorage<T>(key: string) {
  const orgId = useAuthStore((s) => s.org?.id)
  const storageKey = orgId ? `draft:${orgId}:${key}` : null

  function load(): T | null {
    if (!storageKey) return null
    try {
      const raw = localStorage.getItem(storageKey)
      return raw ? (JSON.parse(raw) as T) : null
    } catch {
      return null
    }
  }

  function save(value: T) {
    if (!storageKey) return
    try {
      localStorage.setItem(storageKey, JSON.stringify(value))
    } catch {
      // ignore — autosave is best-effort
    }
  }

  function clear() {
    if (!storageKey) return
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // ignore
    }
  }

  return { load, save, clear }
}
